import { useEffect, useMemo, useState } from 'react';
import { SelectChangeEvent } from '@mui/material';
import Table from '../Table/Table';
import { SORT_DIRECTION_OPTIONS } from '../../../../lib/utils/search/searchHelper';
import { TABLE_LABELS } from '../../../../lib/constants/labelKeys';
import { SortState, TableManagerProps } from './TableManager.types';
import { DEFAULT_SORT_VALUE, sortFunction, sortValue } from './TableManager.utils';
import { TableProps } from '../Table/Table.types';
import useDebouncedCallback from '../../../../lib/hooks/useDebouncedCallback';
import { DEFAULT_DEBOUNCE_TIME } from '../../../../lib/constants/globals';
import { Api } from '../../../../lib/types/apiSpec';

const TableManager = ({ pageSize, data, searchBy, searchPlaceholderKey, defaultSort, ...rest }: TableManagerProps) => {
  const sortItems = sortFunction(data);
  const { headings } = rest;
  const [activePage, setActivePage] = useState(1);
  const [sortState, setSortState] = useState<SortState>(defaultSort ?? DEFAULT_SORT_VALUE);
  const [itemsOnPage, setItemsOnPage] = useState(pageSize);
  const [search, setSearch] = useState<string | null>(searchBy ? '' : null);
  const [dataOrder, setDataOrder] = useState(
    sortItems(Object.keys(data), sortState).slice(
      (activePage - 1) * itemsOnPage,
      (activePage - 1) * itemsOnPage + itemsOnPage,
    ),
  );

  const totalPages = useMemo(
    () =>
      Math.ceil(
        Object.values(data).filter(rowValues => searchBy?.(search ?? '')(rowValues) ?? true).length / itemsOnPage,
      ),
    [itemsOnPage, data, search],
  );

  const activeSortValue = sortValue(
    headings?.[sortState.column] ?? headings?.[DEFAULT_SORT_VALUE.column] ?? '',
    sortState.sortDirection,
  );

  const filteredItems = (searchPhrase: string) =>
    Object.entries(data)
      .filter(([, rowValues]) => searchBy?.(searchPhrase)(rowValues) ?? true)
      .map(([key]) => key);

  useEffect(() => {
    setDataOrder(
      sortItems(search ? filteredItems(search) : Object.keys(data), sortState).slice(
        (activePage - 1) * itemsOnPage,
        (activePage - 1) * itemsOnPage + itemsOnPage,
      ),
    );
  }, [sortState, activePage, itemsOnPage, data]);

  const handleChangeSortState = (event: SelectChangeEvent) => {
    const newSortValues = event.target.value.match(/(\w+)\|(asc|desc)/);

    setSortState(() => ({
      sortDirection: (newSortValues?.[2] ?? DEFAULT_SORT_VALUE.sortDirection) as Api.SortDirection,
      column: headings?.indexOf(newSortValues?.[1] ?? '') ?? DEFAULT_SORT_VALUE.column,
    }));
  };

  const handleUpdateDataBySearch = useDebouncedCallback((newSearchValue: string) => {
    setDataOrder(() => filteredItems(newSearchValue));
  }, DEFAULT_DEBOUNCE_TIME);

  const handleSearch = (newValue: string) => {
    setSearch(newValue);
    handleUpdateDataBySearch(newValue);
  };

  const sortValues = useMemo(
    () =>
      headings?.flatMap(entry =>
        SORT_DIRECTION_OPTIONS.map(dir => ({
          value: sortValue(entry, dir),
          labelKey: TABLE_LABELS.SORT_BY_ITEM(entry),
        })),
      ),
    [headings],
  );

  const searchValue: TableProps['search'] = useMemo(
    () =>
      search !== null
        ? {
            phrase: search,
            handleChange: handleSearch,
            placeholderKey: searchPlaceholderKey,
          }
        : undefined,
    [search],
  );

  useEffect(() => {
    setActivePage(1);
  }, [sortState, itemsOnPage]);

  useEffect(() => {
    setItemsOnPage(pageSize);
  }, [pageSize]);

  useEffect(() => {
    if (totalPages && activePage > totalPages) {
      setActivePage(() => totalPages);
    }
  }, [totalPages]);

  return (
    <Table
      {...rest}
      data={data}
      dataOrder={dataOrder}
      activePage={activePage}
      totalPages={totalPages}
      activeSortValue={activeSortValue}
      handleSortChange={handleChangeSortState}
      handlePageChange={(page: number) => setActivePage(page)}
      handleItemsOnPage={setItemsOnPage}
      sortValues={sortValues}
      pageSize={itemsOnPage}
      search={searchValue}
    />
  );
};

export default TableManager;
